import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import Checkbox from '@/app/components/Checkbox';
import { Input } from '@/app/components/form/Input';
import { Selector } from '@/app/components/form/Selector';
import type { SelectorOption } from '@/app/components/form/Selector/types';
import { SingleDatePicker } from '@/app/components/form/SingleDatePicker';
import { GENDER_OPTIONS } from '@/consts/options';
import type { GenderType } from '@/types/common';
import { formatMMDDYYYYToYYYYMMDD } from '@/utils/formatDate';

interface MainInfoSectionProps {
  onUpdateFormData?: (fieldName: string, fieldValue: unknown) => void;
}

export const MainInfoSection = ({ onUpdateFormData }: MainInfoSectionProps) => {
  const {
    control,
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();

  const gender = watch('primary_passenger.client.gender');
  const isCertify = watch('primary_passenger.client.userHasCertifiedTheInformation');

  const handleCertifyInformationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(`primary_passenger.client.userHasCertifiedTheInformation`, event.target.checked, { shouldValidate: true });
    onUpdateFormData?.('primary_passenger.client.userHasCertifiedTheInformation', event.target.checked);
  };

  const handleSaveOnBlur = (name: string, value: unknown) => {
    onUpdateFormData?.(name, value);
  };

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      <div className="lg:col-span-3 w-2/3">
        <Checkbox
          {...register(`primary_passenger.client.userHasCertifiedTheInformation`)}
          checked={isCertify}
          size="medium"
          variant="transparent"
          onChange={handleCertifyInformationChange}
          label="I certify the information below matches the information that appears on the passenger’s government-issued ID."
        />
      </div>

      <Input
        label="First name"
        placeholder="Enter first name"
        required
        {...register('primary_passenger.client.first_name')}
        errors={errors}
        onBlur={(e) => handleSaveOnBlur(e.target.name, e.target.value)}
      />

      <Input
        label="Middle name"
        placeholder="Enter middle name"
        {...register('primary_passenger.client.middle_name')}
        errors={errors}
        onBlur={(e) => handleSaveOnBlur(e.target.name, e.target.value)}
      />

      <Input
        label="Last name"
        placeholder="Enter last name"
        required
        {...register('primary_passenger.client.last_name')}
        errors={errors}
        onBlur={(e) => handleSaveOnBlur(e.target.name, e.target.value)}
      />

      <div className="lg:col-span-3">
        <Input
          label="Preferred name"
          placeholder="Enter the name you prefer to be addressed by"
          {...register('primary_passenger.client.preferred_name')}
          errors={errors}
          onBlur={(e) => handleSaveOnBlur(e.target.name, e.target.value)}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:col-span-3 lg:grid-cols-2">
        <Controller
          name="primary_passenger.client.date_of_birth"
          control={control}
          render={({ field }) => (
            <SingleDatePicker
              name="primary_passenger.client.date_of_birth"
              label="Date of birth"
              errors={errors}
              value={field.value || ''}
              maxDate={new Date()}
              onBlur={field.onBlur}
              onChangeDate={(date) => {
                field.onChange(date);
                onUpdateFormData?.('primary_passenger.client.date_of_birth', formatMMDDYYYYToYYYYMMDD(date));
              }}
            />
          )}
        />

        <Selector
          className="w-full"
          options={GENDER_OPTIONS}
          value={gender || ''}
          label="Gender"
          onChange={(option) => {
            setValue('primary_passenger.client.gender', (option as SelectorOption<GenderType>).value);
            onUpdateFormData?.('primary_passenger.client.gender', (option as SelectorOption<GenderType>).value);
          }}
          placeholder="Select gender"
          menuPosition="fixed"
          maxMenuHeight={220}
          errors={errors}
          name="primary_passenger.client.gender"
        />
      </div>
    </div>
  );
};
